/**
 * Phase 15.4 — Driver Trip Log v2
 *
 * Flow:
 *   1. Auto-detect primary truck (driver can override)
 *   2. Trip Start: radio (Unit 1 / Unit 2 / Other depot or client) + start odometer
 *   3. Destination: searchable client list with address, contact, click-to-call,
 *      Google Maps deep-link, and a "+ Add new location" inline button
 *   4. End odometer + photos + notes
 *   5. Submit
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Truck, MapPin, Camera, CheckCircle2, ArrowRight, ArrowLeft, X, Plus,
  Loader2, IndianRupee, Calendar, Phone, Navigation, Search, Building2,
} from "lucide-react";

const STEPS = [
  { id: 1, label: "Truck & Start" },
  { id: 2, label: "Destination" },
  { id: 3, label: "Finish" },
  { id: 4, label: "Photos" },
  { id: 5, label: "Review" },
];

const inputCls =
  "w-full rounded-xl bg-white border border-slate-300 px-4 py-3 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-bravura focus:border-bravura";

function StepBar({ step }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-1.5">
        {STEPS.map((s) => (
          <div key={s.id}
               className={`h-1.5 rounded-full transition-all ${
                 s.id < step ? "bg-bravura-500 w-6" :
                 s.id === step ? "bg-bravura-500 w-10" : "bg-slate-200 w-4"
               }`} />
        ))}
      </div>
      <div className="text-xs text-slate-500 font-medium">Step {step}/{STEPS.length}</div>
    </div>
  );
}

function FieldLabel({ children, required }) {
  return (
    <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1.5">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

export default function TripLog() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [trucks, setTrucks] = useState([]);
  const [primaryTruck, setPrimaryTruck] = useState(null);
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [addLocOpen, setAddLocOpen] = useState(false);

  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    truck_id: "",
    start_choice: "unit1",       // unit1 | unit2 | other
    start_location_id: "",
    location_id: "",             // destination
    start_odometer: "",
    end_odometer: "",
    end_latitude: null,
    end_longitude: null,
    bill_photos: ["", "", "", ""],
    notes: "",
  });
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const loadLocations = async () => {
    try {
      const { data } = await api.get("/logistics/locations/for-driver");
      setLocations(data || []);
    } catch {
      // toast already on failure elsewhere
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [tr, pr, loc] = await Promise.all([
          api.get("/fuel/my-trucks"),
          api.get("/logistics/trucks/my-primary").catch(() => ({ data: {} })),
          api.get("/logistics/locations/for-driver"),
        ]);
        setTrucks(tr.data || []);
        const pt = pr.data?.truck;
        setPrimaryTruck(pt || null);
        setLocations(loc.data || []);
        if (pt?.id) update({ truck_id: pt.id });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load data");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve start_location_id from depots when start_choice changes
  useEffect(() => {
    if (form.start_choice === "other") return;
    const target = form.start_choice === "unit1" ? "Unit 1" : "Unit 2";
    const depot = locations.find(
      (l) => l.location_type === "depot" && l.name === target,
    );
    if (depot) update({ start_location_id: depot.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.start_choice, locations]);

  const canProceed = () => {
    if (step === 1) {
      return !!form.truck_id && !!form.date && !!form.start_location_id
             && !!form.start_odometer;
    }
    if (step === 2) return !!form.location_id;
    if (step === 3) return true; // end odo optional
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        date: form.date,
        truck_id: form.truck_id,
        location_id: form.location_id,
        start_location_id: form.start_location_id,
        start_odometer: form.start_odometer ? Number(form.start_odometer) : null,
        end_odometer: form.end_odometer ? Number(form.end_odometer) : null,
        end_latitude: form.end_latitude,
        end_longitude: form.end_longitude,
        rto: 0, cash: 0, tolls: 0, dala: 0, unloading: 0, other: 0,
        other_remark: null,
        bill_photos: form.bill_photos.filter(Boolean),
        notes: form.notes || null,
      };
      const { data } = await api.post("/logistics/trips/by-driver", payload);
      toast.success("Trip logged ✓");
      navigate(`/driver/trips/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save trip");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4" data-testid="trip-log-wizard">
      <StepBar step={step} />

      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 shadow-sm">
        {step === 1 && (
          <Step1
            form={form} update={update}
            trucks={trucks} primaryTruck={primaryTruck}
            locations={locations}
          />
        )}
        {step === 2 && (
          <Step2
            form={form} update={update}
            locations={locations}
            onAdd={() => setAddLocOpen(true)}
          />
        )}
        {step === 3 && <Step3 form={form} update={update} />}
        {step === 4 && <Step4 form={form} update={update} />}
        {step === 5 && (
          <Step5 form={form} trucks={trucks} locations={locations} />
        )}
      </div>

      <div className="flex gap-3">
        {step > 1 && (
          <button onClick={() => setStep((s) => Math.max(1, s - 1))}
                  data-testid="trip-back-btn"
                  className="flex-1 rounded-xl bg-slate-100 text-slate-700 px-4 py-3.5 font-semibold flex items-center justify-center gap-1.5 active:scale-[0.99]">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        )}
        {step < STEPS.length && (
          <button
            onClick={() => canProceed() ? setStep((s) => s + 1) : toast.warning("Please fill required fields")}
            data-testid="trip-next-btn"
            disabled={!canProceed()}
            className={`flex-1 rounded-xl px-4 py-3.5 font-semibold flex items-center justify-center gap-1.5 active:scale-[0.99] transition ${
              canProceed() ? "bg-bravura text-white shadow-md" : "bg-slate-200 text-slate-400"
            }`}>
            Next <ArrowRight className="h-4 w-4" />
          </button>
        )}
        {step === STEPS.length && (
          <button onClick={submit} disabled={submitting}
                  data-testid="trip-submit-btn"
                  className="flex-1 rounded-xl bg-emerald-600 text-white px-4 py-3.5 font-semibold flex items-center justify-center gap-2 active:scale-[0.99] shadow-md disabled:opacity-60">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Log Trip
          </button>
        )}
      </div>

      {addLocOpen && (
        <AddLocationModal
          onClose={() => setAddLocOpen(false)}
          onSubmitted={() => { setAddLocOpen(false); loadLocations(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Step 1 — Date, Truck, Start (Unit 1 / Unit 2 / Other) + start odometer
// =============================================================================
function Step1({ form, update, trucks, primaryTruck, locations }) {
  const depots = locations.filter((l) => l.location_type === "depot");
  const otherLocs = locations.filter((l) => l.location_type !== "depot");

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-slate-900 text-bravura-300 flex items-center justify-center">
          <Truck className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Trip start
        </h2>
      </div>

      <div>
        <FieldLabel required>Date</FieldLabel>
        <input type="date" className={inputCls} value={form.date}
               onChange={(e) => update({ date: e.target.value })}
               data-testid="trip-date" />
      </div>

      <div>
        <FieldLabel required>Truck</FieldLabel>
        {primaryTruck && form.truck_id === primaryTruck.id && (
          <div className="text-[11px] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-md px-2 py-1 mb-1.5">
            <CheckCircle2 className="h-3 w-3 inline mr-1" />
            Auto-selected: <b>{primaryTruck.truck_no}</b> (your assigned truck)
          </div>
        )}
        <select className={inputCls} value={form.truck_id}
                onChange={(e) => update({ truck_id: e.target.value })}
                data-testid="trip-truck">
          <option value="">Select truck…</option>
          {trucks.map((t) => (
            <option key={t.id} value={t.id}>
              {t.truck_no} {t.model ? `— ${t.model}` : ""}
              {primaryTruck && primaryTruck.id === t.id ? " ★" : ""}
            </option>
          ))}
        </select>
        {primaryTruck && form.truck_id !== primaryTruck.id && form.truck_id && (
          <p className="text-[10px] text-amber-700 mt-1">
            ℹ Aap ne different truck select kiya hai. Trip log karte hi yeh truck
            aapki primary ban jaayegi (LM update).
          </p>
        )}
      </div>

      <div>
        <FieldLabel required>Trip starts from</FieldLabel>
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: "unit1", label: "Unit 1" },
            { v: "unit2", label: "Unit 2" },
            { v: "other", label: "Other" },
          ].map((opt) => (
            <button key={opt.v}
                    onClick={() => update({ start_choice: opt.v, ...(opt.v !== "other" ? {} : { start_location_id: "" }) })}
                    data-testid={`start-${opt.v}`}
                    className={`rounded-xl py-3 text-sm font-semibold transition active:scale-95 ${
                      form.start_choice === opt.v
                        ? "bg-bravura text-white shadow"
                        : "bg-slate-100 text-slate-600"
                    }`}>
              <Building2 className="h-4 w-4 inline mr-1" />
              {opt.label}
            </button>
          ))}
        </div>
        {form.start_choice === "other" && (
          <select className={`${inputCls} mt-2`} value={form.start_location_id}
                  onChange={(e) => update({ start_location_id: e.target.value })}
                  data-testid="start-other-select">
            <option value="">Choose start location…</option>
            {[...depots, ...otherLocs].map((l) => (
              <option key={l.id} value={l.id}>{l.label || l.name}</option>
            ))}
          </select>
        )}
        {form.start_choice !== "other" && !form.start_location_id && (
          <p className="text-[10px] text-red-600 mt-1">
            Depot location is being looked up… (yeh init pe set hota hai)
          </p>
        )}
      </div>

      <div>
        <FieldLabel required>Start Odometer (km)</FieldLabel>
        <input type="number" inputMode="decimal" className={inputCls}
               value={form.start_odometer} placeholder="e.g. 125450"
               onChange={(e) => update({ start_odometer: e.target.value })}
               data-testid="trip-start-odo" />
      </div>
    </div>
  );
}

// =============================================================================
// Step 2 — Destination with contact + Maps + Add location button
// =============================================================================
function Step2({ form, update, locations, onAdd }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const arr = locations.filter((l) => l.location_type !== "depot");
    if (!term) return arr;
    return arr.filter((l) =>
      (l.name || "").toLowerCase().includes(term)
      || (l.name_hindi || "").toLowerCase().includes(term)
      || (l.address || "").toLowerCase().includes(term)
      || (l.client_code || "").toLowerCase().includes(term)
    );
  }, [q, locations]);
  const selected = locations.find((l) => l.id === form.location_id);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-bravura text-white flex items-center justify-center">
          <MapPin className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Destination
        </h2>
      </div>

      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className={`${inputCls} pl-10`} placeholder="Search client / city…"
               value={q} onChange={(e) => setQ(e.target.value)}
               data-testid="dest-search" />
      </div>

      <button onClick={onAdd}
              data-testid="add-new-location-btn"
              className="w-full rounded-xl border-2 border-dashed border-bravura-300 bg-bravura-50 text-bravura-700 py-3 font-semibold flex items-center justify-center gap-1.5 active:scale-[0.99]">
        <Plus className="h-4 w-4" /> Add new location (on the spot)
      </button>

      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {filtered.slice(0, 80).map((l) => (
          <button key={l.id}
                  onClick={() => update({ location_id: l.id })}
                  data-testid={`dest-opt-${l.id}`}
                  className={`w-full text-left rounded-xl p-3 ring-1 transition ${
                    form.location_id === l.id
                      ? "bg-bravura-50 ring-bravura-400"
                      : "bg-white ring-slate-200 active:bg-slate-50"
                  }`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{l.name}</div>
                {l.name_hindi && (
                  <div className="text-xs text-slate-500 truncate">{l.name_hindi}</div>
                )}
                {l.address && (
                  <div className="text-[11px] text-slate-600 truncate mt-0.5">{l.address}</div>
                )}
              </div>
              {form.location_id === l.id && (
                <CheckCircle2 className="h-5 w-5 text-bravura-700" />
              )}
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="text-center text-xs text-slate-500 py-4">No matches.</div>
        )}
      </div>

      {selected && (
        <div className="rounded-2xl bg-slate-900 text-white p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            Selected destination
          </div>
          <div className="text-base font-bold">{selected.name}</div>
          {selected.address && (
            <div className="text-xs text-slate-300">{selected.address}</div>
          )}
          <div className="grid grid-cols-2 gap-2 mt-2">
            {selected.contact_phone ? (
              <a href={`tel:${selected.contact_phone}`}
                 data-testid="call-contact-btn"
                 className="rounded-xl bg-emerald-600 text-white px-3 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-95">
                <Phone className="h-3.5 w-3.5" />
                Call {selected.contact_name || ""}
              </a>
            ) : <div className="text-[10px] text-slate-400 self-center text-center">No contact phone</div>}
            {selected.latitude != null && selected.longitude != null ? (
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${selected.latitude},${selected.longitude}`}
                 target="_blank" rel="noreferrer"
                 data-testid="open-maps-btn"
                 className="rounded-xl bg-bravura-500 text-white px-3 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-95">
                <Navigation className="h-3.5 w-3.5" /> Open in Maps
              </a>
            ) : <div className="text-[10px] text-slate-400 self-center text-center">No GPS coords</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Step 3 — End odometer + (auto GPS capture) finish info
// =============================================================================
function Step3({ form, update }) {
  const captureGps = () => {
    if (!navigator.geolocation) {
      toast.warning("GPS not supported on this device");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        update({
          end_latitude: pos.coords.latitude,
          end_longitude: pos.coords.longitude,
        });
        toast.success("Location captured ✓");
      },
      () => toast.error("Could not get location"),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };
  const startN = parseFloat(form.start_odometer);
  const endN = parseFloat(form.end_odometer);
  const dist = (isFinite(startN) && isFinite(endN) && endN >= startN)
    ? (endN - startN).toFixed(1) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
          <Navigation className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Finish trip
        </h2>
      </div>

      <div>
        <FieldLabel>End Odometer (km)</FieldLabel>
        <input type="number" inputMode="decimal" className={inputCls}
               value={form.end_odometer}
               placeholder="Leave blank if not yet at destination"
               onChange={(e) => update({ end_odometer: e.target.value })}
               data-testid="trip-end-odo" />
      </div>

      {dist && (
        <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2 text-sm text-emerald-800">
          Distance covered: <b>{dist} km</b>
        </div>
      )}

      <button onClick={captureGps}
              data-testid="capture-gps-btn"
              className="w-full rounded-xl bg-slate-900 text-white py-3 text-sm font-semibold flex items-center justify-center gap-1.5 active:scale-[0.99]">
        <MapPin className="h-4 w-4" />
        {form.end_latitude != null
          ? `GPS captured (${form.end_latitude.toFixed(4)}, ${form.end_longitude.toFixed(4)})`
          : "Capture finish GPS (optional)"}
      </button>
    </div>
  );
}

// =============================================================================
// Step 4 — Photos & notes (kept from v1)
// =============================================================================
const PHOTO_LABELS = ["Receipt 1", "Receipt 2", "POD / e-way Bill", "Other"];

function Step4({ form, update }) {
  const setPhoto = (idx, d) => {
    const arr = [...form.bill_photos];
    arr[idx] = d;
    update({ bill_photos: arr });
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-slate-900 text-bravura-300 flex items-center justify-center">
          <Camera className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Bill / POD Photos
        </h2>
      </div>
      <p className="text-xs text-slate-500 -mt-2">All optional — capture what you have</p>

      <div className="grid grid-cols-2 gap-3">
        {PHOTO_LABELS.map((lbl, idx) => (
          <PhotoTile key={idx} label={lbl} value={form.bill_photos[idx]}
                     onCapture={(d) => setPhoto(idx, d)}
                     onClear={() => setPhoto(idx, "")}
                     testId={`trip-photo-${idx}`} />
        ))}
      </div>

      <div>
        <FieldLabel>Notes (optional)</FieldLabel>
        <textarea rows={2} className={inputCls} value={form.notes}
                  onChange={(e) => update({ notes: e.target.value })}
                  placeholder="Any remark for accounts"
                  data-testid="trip-notes" />
      </div>
    </div>
  );
}

function PhotoTile({ label, value, onCapture, onClear, testId }) {
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const MAX_W = 900;
        const scale = Math.min(1, MAX_W / img.width);
        const c = document.createElement("canvas");
        c.width = img.width * scale; c.height = img.height * scale;
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        onCapture(c.toDataURL("image/jpeg", 0.7));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };
  return (
    <label className="relative block cursor-pointer">
      <div className={`w-full aspect-square rounded-2xl border-2 border-dashed transition flex flex-col items-center justify-center gap-1 ${
        value ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-slate-50"
      }`}>
        {value
          ? <img src={value} alt={label} className="w-full h-full object-cover rounded-2xl" />
          : <>
              <Camera className="h-7 w-7 text-slate-400" />
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
              <Plus className="h-4 w-4 text-bravura" />
            </>}
      </div>
      {value && (
        <button onClick={(e) => { e.preventDefault(); onClear(); }}
                className="absolute top-1 right-1 h-7 w-7 rounded-full bg-red-600 text-white flex items-center justify-center shadow"
                data-testid={`${testId}-clear`}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      <input type="file" accept="image/*"
             onChange={onPick} className="sr-only" data-testid={testId} />
    </label>
  );
}

// =============================================================================
// Step 5 — Review
// =============================================================================
function Step5({ form, trucks, locations }) {
  const truck = trucks.find((t) => t.id === form.truck_id);
  const start = locations.find((l) => l.id === form.start_location_id);
  const dest = locations.find((l) => l.id === form.location_id);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Review & Submit
        </h2>
      </div>

      <div className="rounded-2xl bg-slate-900 text-white p-4 space-y-2">
        <Row k="Date" v={form.date} icon={Calendar} />
        <Row k="Truck" v={truck?.truck_no || "—"} icon={Truck} />
        <Row k="From" v={start?.name || "—"} icon={Building2} />
        <Row k="To" v={dest?.name || "—"} icon={MapPin} />
        <Row k="Start km" v={form.start_odometer || "—"} />
        <Row k="End km" v={form.end_odometer || "—"} />
      </div>

      {form.bill_photos.filter(Boolean).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
            Photos ({form.bill_photos.filter(Boolean).length}/4)
          </div>
          <div className="grid grid-cols-4 gap-2">
            {form.bill_photos.map((p, i) => p && (
              <img key={i} src={p} alt="" className="aspect-square object-cover rounded-lg ring-1 ring-slate-200" />
            ))}
          </div>
        </div>
      )}

      <div className="text-[11px] text-bravura-700 bg-bravura-50 ring-1 ring-bravura-200 rounded-lg px-3 py-2 space-y-1">
        <div>ℹ Trip <b>Open</b> status mein save hoga. Logistics Manager
          baad mein settle karega.</div>
        <div>↻ Return trip lana ho toh trip detail page se "Log Market Return" use karein.</div>
      </div>
    </div>
  );
}

function Row({ k, v, icon: Icon }) {
  return (
    <div className="flex items-center justify-between text-sm gap-3">
      <span className="text-slate-400 flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {k}
      </span>
      <span className="font-semibold text-right">{v}</span>
    </div>
  );
}

// =============================================================================
// Add-new-location modal (driver creates a draft → admin approves)
// =============================================================================
function AddLocationModal({ onClose, onSubmitted }) {
  const [f, setF] = useState({
    name: "", address: "", contact_name: "", contact_phone: "",
    latitude: null, longitude: null, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);

  const capture = () => {
    if (!navigator.geolocation) { toast.warning("GPS not supported"); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setF((x) => ({ ...x, latitude: p.coords.latitude, longitude: p.coords.longitude }));
        toast.success("GPS captured ✓");
        setGpsBusy(false);
      },
      () => { toast.error("Could not get location"); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const submit = async () => {
    if (!f.name.trim()) { toast.warning("Name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/logistics/locations/draft-by-driver", f);
      if (data?.duplicate) {
        toast.warning(data.message || "Location already exists");
      } else {
        toast.success("Submitted for approval ✓");
      }
      onSubmitted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center sm:justify-center p-0 sm:p-4"
         data-testid="add-loc-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">Add new location</h3>
          <button onClick={onClose} className="p-1"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-md p-2">
          ⚠ Approval ke baad list mein dikhega. Admin verify karke active karega.
        </p>
        <div>
          <FieldLabel required>Location name</FieldLabel>
          <input className={inputCls} value={f.name}
                 onChange={(e) => setF({ ...f, name: e.target.value })}
                 placeholder="e.g. Sharma Traders, Indore Market"
                 data-testid="add-loc-name" />
        </div>
        <div>
          <FieldLabel>Address</FieldLabel>
          <textarea rows={2} className={inputCls} value={f.address}
                    onChange={(e) => setF({ ...f, address: e.target.value })}
                    data-testid="add-loc-address" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <FieldLabel>Contact name</FieldLabel>
            <input className={inputCls} value={f.contact_name}
                   onChange={(e) => setF({ ...f, contact_name: e.target.value })}
                   data-testid="add-loc-contact-name" />
          </div>
          <div>
            <FieldLabel>Phone</FieldLabel>
            <input className={inputCls} value={f.contact_phone} type="tel"
                   onChange={(e) => setF({ ...f, contact_phone: e.target.value })}
                   data-testid="add-loc-contact-phone" />
          </div>
        </div>
        <button onClick={capture} disabled={gpsBusy}
                data-testid="add-loc-gps"
                className="w-full rounded-xl bg-slate-900 text-white py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60">
          {gpsBusy && <Loader2 className="h-4 w-4 animate-spin" />}
          <MapPin className="h-4 w-4" />
          {f.latitude != null
            ? `GPS: ${f.latitude.toFixed(4)}, ${f.longitude.toFixed(4)}`
            : "Capture current location (GPS)"}
        </button>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose}
                  className="flex-1 rounded-xl bg-slate-100 text-slate-700 py-2.5 font-semibold">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="add-loc-submit"
                  className="flex-1 rounded-xl bg-bravura text-white py-2.5 font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Submit for approval
          </button>
        </div>
      </div>
    </div>
  );
}
